import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaArrowRight, FaCheck, FaBuilding, FaWrench,
  FaComments, FaCalendarAlt, FaChevronDown, FaKey,
  FaChartLine, FaLayerGroup, FaHome, FaCreditCard
} from 'react-icons/fa';
import logo from '../Assets/RSIDE.png';
import rsideHome from '../Assets/rside_home.png';
import propertiesImg from '../Assets/properties.png';
import appPic from '../Assets/AppPic.png';
import propertiesApp from '../Assets/PropertiesApp.png';
import reportsApp from '../Assets/ReportsApp.png';
import activityApp from '../Assets/ActivityApp.png';
import workOrderApp from '../Assets/WorkOrderApp.png';
import tenantHomeApp from '../Assets/TenantHomeApp.png';
import tenantRequestsApp from '../Assets/TenantRequestsApp.png';
import tenantHome from '../Assets/rside.app_tenant_home.png';
import './LandingPage.css';

const APP_SLIDES = [
  { src: appPic,             type: 'manager' },
  { src: propertiesApp,      type: 'manager' },
  { src: reportsApp,         type: 'manager' },
  { src: activityApp,        type: 'manager' },
  { src: workOrderApp,       type: 'manager' },
  { src: tenantHomeApp,      type: 'tenant'  },
  { src: tenantRequestsApp,  type: 'tenant'  },
];

const MANAGER_BULLETS = [
  'See rent, expenses, and cash flow on the go',
  'Full portfolio overview — all your units at a glance',
  'Track work orders and maintenance from anywhere',
  'Access everything from your phone or desktop',
];
const TENANT_BULLETS = [
  'See when rent is due and what you\'ve paid',
  'Find your lease and property info instantly',
  'Submit and track maintenance requests',
  'No calls, no emails — just your info, on your phone',
];

const FAQ_ITEMS = [
  { q: 'Who is RSIDE for?', a: 'Anyone managing rentals and anyone renting. Managers use it to stay on top of everything across their portfolio. Tenants use it to see what matters about their rental without having to ask.' },
  { q: 'What can property managers track?', a: 'Rent collected and outstanding, expenses by property, open work orders, lease end dates, and how cash flow is trending — it\'s all on the dashboard.' },
  { q: 'What can tenants do in the portal?', a: 'See when rent is due and what they\'ve paid, find their lease and property details, and submit or follow up on maintenance requests.' },
  { q: 'How does the manager and tenant access work?', a: 'Managers set everything up on their side. When ready, they invite tenants. Each tenant logs into their own portal — separate from the manager side but tied to the same data.' },
  { q: 'Is RSIDE free to get started?', a: 'Yes, free during open beta. No card needed, no time limit. Just sign up and start.' },
];

const FEATURES = [
  { num: '01', icon: <FaLayerGroup />, color: '#818cf8', title: 'Portfolio overview', desc: 'See which units are occupied, which rent has landed, and what needs attention — all without digging.', tag: 'Property performance' },
  { num: '02', icon: <FaCreditCard />, color: '#34d399', title: 'Rent & payments', desc: 'Keep track of what\'s been paid, what\'s overdue, and what\'s coming up. Both sides see the same record.', tag: 'Clear payment status' },
  { num: '03', icon: <FaCalendarAlt />, color: '#818cf8', title: 'Leases & deadlines', desc: 'Know when leases are ending and when action is needed — before the deadline sneaks up.', tag: 'Nothing slips past' },
  { num: '04', icon: <FaWrench />, color: '#818cf8', title: 'Maintenance', desc: 'Tenants submit requests, you track progress. Nothing gets buried in a text thread.', tag: 'Track every work order' },
  { num: '05', icon: <FaChartLine />, color: '#34d399', title: 'Expenses & cash flow', desc: 'Log expenses by property, see where money\'s going, and match it against what\'s coming in.', tag: 'Know where money moves' },
  { num: '06', icon: <FaComments />, color: '#818cf8', title: 'Communication', desc: 'Keep messages tied to the property, lease, or work order they\'re actually about.', tag: 'Context stays connected' },
];

function FaqItem({ q, a, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen || false);
  return (
    <div className={`lp-faq-item${open ? ' lp-faq-open' : ''}`} onClick={() => setOpen(v => !v)}>
      <div className="lp-faq-row">
        <span className="lp-faq-q">{q}</span>
        <FaChevronDown className={`lp-faq-icon${open ? ' lp-faq-icon-open' : ''}`} />
      </div>
      {open && <p className="lp-faq-a">{a}</p>}
    </div>
  );
}

function LandingPage() {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [appSlide, setAppSlide] = useState(0);
  const dragX = useRef(null);

  const slideCount = APP_SLIDES.length;
  const prevSlide  = (appSlide - 1 + slideCount) % slideCount;
  const nextSlide  = (appSlide + 1) % slideCount;
  const isTenant   = APP_SLIDES[appSlide].type === 'tenant';

  const onDragStart = (e) => {
    dragX.current = e.type === 'touchstart' ? e.touches[0].clientX : e.clientX;
  };
  const onDragEnd = (e) => {
    if (dragX.current === null) return;
    const x = e.type === 'touchend' ? e.changedTouches[0].clientX : e.clientX;
    const diff = dragX.current - x;
    if (Math.abs(diff) > 40) setAppSlide(diff > 0 ? nextSlide : prevSlide);
    dragX.current = null;
  };

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', h, { passive: true });
    return () => window.removeEventListener('scroll', h);
  }, []);


  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  return (
    <div className="lp-root">

      {/* ── NAV ── */}
      <nav className={`lp-nav${scrolled ? ' lp-nav-solid' : ''}`}>
        <img src={logo} alt="RSIDE" className="lp-nav-logo" />
        <div className="lp-nav-links">
          <button className="lp-nav-link" onClick={() => scrollTo('for-managers')}>For Managers</button>
          <button className="lp-nav-link" onClick={() => scrollTo('tenant-experience')}>For Tenants</button>
          <button className="lp-nav-link" onClick={() => scrollTo('features')}>Features</button>
          <button className="lp-nav-link" onClick={() => scrollTo('how-it-works')}>How It Works</button>
          <button className="lp-nav-link" onClick={() => scrollTo('faq')}>FAQ</button>
        </div>
        <div className="lp-nav-actions">
          <button className="lp-nav-btn-ghost" onClick={() => navigate('/select-role?mode=login')}>Log In</button>
          <button className="lp-nav-btn-primary" onClick={() => navigate('/select-role?mode=signup')}>
            Get Started <FaArrowRight style={{ fontSize: 11 }} />
          </button>
        </div>
      </nav>

      {/* ── HERO ── */}
      <section className="lp-hero">
        <div className="lp-hero-inner">
          <div className="lp-hero-left">
            <div className="lp-badge lp-badge-dark lp-hero-anim" style={{ animationDelay: '0ms' }}>
              <span className="lp-badge-dot lp-dot-green" />
              OPEN BETA — FREE TO GET STARTED
            </div>
            <h1 className="lp-hero-h1 lp-hero-anim" style={{ animationDelay: '80ms' }}>
               Your properties,<br />Your tenants,<br />One simple platform.
            </h1>
            <p className="lp-hero-sub lp-hero-anim" style={{ animationDelay: '180ms' }}>
              Rent, leases, maintenance, expenses, and a built-in tenant portal — all connected in one place. No more switching between apps to figure out what's going on.
            </p>
            <div className="lp-hero-ctas lp-hero-anim" style={{ animationDelay: '280ms' }}>
              <button className="lp-btn-primary-hero" onClick={() => navigate('/signup')}>
                Start as a Manager <FaArrowRight style={{ fontSize: 12 }} />
              </button>
              <button className="lp-btn-ghost-hero" onClick={() => scrollTo('for-managers')}>
                Explore the Product <FaArrowRight style={{ fontSize: 12 }} />
              </button>
            </div>
            <div className="lp-hero-trust">
              <span className="lp-trust-item"><FaCheck className="lp-trust-icon" /> No credit card required</span>
              <span className="lp-trust-sep">·</span>
              <button className="lp-trust-link" onClick={() => navigate('/tenant/login')}>
                <FaKey style={{ fontSize: 11 }} /> Tenant? Log in to your portal
              </button>
            </div>
          </div>

          <div className="lp-hero-right lp-hero-anim" style={{ animationDelay: '200ms' }}>
            <div className="lp-hero-img-wrap">
              <img src={rsideHome} alt="RSIDE dashboard" className="lp-hero-img" />
            </div>
          </div>
        </div>
      </section>

      {/* ── CHOOSE YOUR SIDE ── */}
      <section className="lp-two-sides" id="choose-side">
        <div className="lp-section-inner">
          <div className="lp-section-header lp-header-center">
            <div className="lp-badge lp-badge-light">
              <span className="lp-badge-dot lp-dot-purple" />
              WHO IT'S FOR
            </div>
            <h2 className="lp-section-h2 lp-h2-dark">Two experiences.<br />One shared source of truth.</h2>
            <p className="lp-section-sub lp-sub-dark">
              Managers get a full view of every property. Tenants get a simple place for rent,
              documents, and requests — tied to the same data.
            </p>
          </div>

          <div className="lp-sides-grid">
            <div className="lp-side-card lp-side-dark">
              <div className="lp-side-top">
                <div className="lp-side-icon-wrap lp-icon-dark"><FaBuilding /></div>
                <span className="lp-side-tag">BUILT TO RUN YOUR PORTFOLIO</span>
              </div>
              <div className="lp-side-role">PROPERTY MANAGERS</div>
              <h3 className="lp-side-h3">See the whole portfolio — and what needs attention next.</h3>
              <p className="lp-side-desc">Rent, leases, deadlines, expenses, work orders — everything you're tracking, pulled into one place.</p>
              <ul className="lp-side-list">
                {['Track every property and lease', 'Monitor rent, expenses, and cash flow', 'Stay ahead of deadlines and maintenance'].map(t => (
                  <li key={t}><FaCheck className="lp-check-icon lp-check-purple" />{t}</li>
                ))}
              </ul>
              <div className="lp-side-footer">
                <button className="lp-btn-side-primary" onClick={() => navigate('/signup')}>
                  Get Started as a Manager <FaArrowRight style={{ fontSize: 11 }} />
                </button>
                <span className="lp-side-note">Free during open beta</span>
              </div>
            </div>

            <div className="lp-side-card lp-side-light">
              <div className="lp-side-top">
                <div className="lp-side-icon-wrap lp-icon-teal"><FaKey /></div>
              </div>
              <div className="lp-side-role lp-side-role-teal">TENANTS</div>
              <h3 className="lp-side-h3 lp-side-h3-dark">Everything about your rental, without the back-and-forth.</h3>
              <p className="lp-side-desc lp-side-desc-dark">One spot for rent, lease details, property info, and maintenance updates. No more hunting through emails.</p>
              <ul className="lp-side-list lp-side-list-dark">
                {['See rent due dates and payment history', 'Find your lease and property details', 'Submit and track maintenance requests'].map(t => (
                  <li key={t}><FaCheck className="lp-check-icon lp-check-teal" />{t}</li>
                ))}
              </ul>
              <div className="lp-side-footer">
                <button className="lp-btn-side-ghost" onClick={() => navigate('/tenant/signup')}>
                  Sign Up as a Tenant <FaArrowRight style={{ fontSize: 11 }} />
                </button>
                <span className="lp-side-note lp-side-note-gray">You'll need an invite from your manager</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section className="lp-hiw" id="how-it-works">
        <div className="lp-section-inner">
          <div className="lp-section-header lp-header-center">
            <div className="lp-badge lp-badge-light">
              <span className="lp-badge-dot lp-dot-purple" />
              HOW IT WORKS
            </div>
            <h2 className="lp-section-h2 lp-h2-dark">Set up the basics.<br />Go from there.</h2>
            <p className="lp-section-sub lp-sub-dark">Most managers are up and running the same day.<br />No onboarding calls, no setup fees.</p>
            <button className="lp-btn-outline-dark" style={{ marginTop: 8 }} onClick={() => navigate('/signup')}>
              Start Free <FaArrowRight style={{ fontSize: 11 }} />
            </button>
          </div>

          <div className="lp-steps-row">
            {[
              { num: '01', icon: <FaHome />, title: 'Create your manager workspace', desc: 'Sign up free — no credit card, no time limit.' },
              { num: '02', icon: <FaBuilding />, title: 'Add your properties and leases', desc: 'Add properties and fill in the basics for each lease.' },
              { num: '03', icon: <FaKey />, title: 'Invite tenants when you\'re ready', desc: 'Send a link. Tenants get their own portal, you stay connected.' },
            ].map((s) => (
              <div key={s.num} className="lp-step">
                <div className="lp-step-icon-wrap" data-num={s.num}><div className="lp-step-icon">{s.icon}</div></div>
                <div className="lp-step-num">{s.num}</div>
                <h3 className="lp-step-title">{s.title}</h3>
                <p className="lp-step-desc">{s.desc}</p>
              </div>
            ))}
          </div>

          <div className="lp-hiw-cta-card">
            <div className="lp-hiw-cta-left">
              <div className="lp-badge lp-badge-dark-sm">
                <span className="lp-badge-dot lp-dot-green" />
                OPEN BETA
              </div>
              <h3 className="lp-hiw-cta-title">Most managers are set up within a day.</h3>
              <p className="lp-hiw-cta-sub">RSIDE is free during open beta. Start with one property, add more as you go. No commitment required.</p>
            </div>
            <div className="lp-hiw-cta-right">
              <button className="lp-btn-cta-primary" onClick={() => navigate('/signup')}>
                Create Manager Workspace <FaArrowRight style={{ fontSize: 11 }} />
              </button>
              <div className="lp-hiw-cta-note"><FaCheck style={{ fontSize: 10 }} /> No credit card required</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FOR MANAGERS ── */}
      <section className="lp-for-managers" id="for-managers">
        <div className="lp-section-inner lp-split-section">
          <div className="lp-split-left">
            <div className="lp-badge lp-badge-light">
              <span className="lp-badge-dot lp-dot-teal" />
              FOR PROPERTY MANAGERS
            </div>
            <h2 className="lp-section-h2 lp-h2-dark">Operate with context,<br />not scattered tabs.</h2>
            <p className="lp-section-sub lp-sub-dark">
              Stop switching between apps to figure out what's going on.
              RSIDE keeps your rent, leases, expenses, and work orders together so nothing slips through.
            </p>
            <ul className="lp-feature-list">
              {['Jump from portfolio view to property detail in one click', 'Lease deadlines and open maintenance stay visible', 'Cash flow that doesn\'t require a spreadsheet'].map(t => (
                <li key={t}><FaCheck className="lp-fl-check" />{t}</li>
              ))}
            </ul>
            <button className="lp-btn-outline-dark" onClick={() => navigate('/signup')}>
              Get Started as a Manager <FaArrowRight style={{ fontSize: 11 }} />
            </button>
          </div>

          <div className="lp-split-right">
            <div className="lp-section-img-wrap">
              <img src={propertiesImg} alt="RSIDE properties dashboard" className="lp-section-img" />
            </div>
          </div>
        </div>
      </section>

      {/* ── FOR TENANTS (app carousel) ── */}
      <section className="lp-for-tenants" id="for-tenants">
        <div className="lp-section-inner lp-split-section lp-split-app">

          {/* carousel column */}
          <div className="lp-split-left lp-app-pic-col">
            <div
              className="lp-carousel-wrap"
              onMouseDown={onDragStart}
              onMouseUp={onDragEnd}
              onTouchStart={onDragStart}
              onTouchEnd={onDragEnd}
            >
              {APP_SLIDES.map((slide, i) => {
                let pos = 'hidden';
                if (i === appSlide) pos = 'center';
                else if (i === prevSlide) pos = 'left';
                else if (i === nextSlide) pos = 'right';
                return (
                  <div
                    key={i}
                    className={`lp-carousel-slide lp-cs-${pos}`}
                    onClick={() => {
                      if (pos === 'left')  setAppSlide(prevSlide);
                      if (pos === 'right') setAppSlide(nextSlide);
                    }}
                  >
                    <img src={slide.src} alt="" className="lp-carousel-img" draggable={false} />
                  </div>
                );
              })}
            </div>
            <div className="lp-carousel-dots">
              {APP_SLIDES.map((_, i) => (
                <button
                  key={i}
                  className={`lp-carousel-dot${i === appSlide ? ' lp-dot-active' : ''}`}
                  onClick={() => setAppSlide(i)}
                />
              ))}
            </div>
          </div>

          {/* dynamic text column */}
          <div className="lp-split-right">
            <div className="lp-badge lp-badge-light">
              <span className="lp-badge-dot lp-dot-purple" />
              {isTenant ? 'TENANT MOBILE APP' : 'MANAGER MOBILE APP'}
            </div>
            <h2 className="lp-section-h2 lp-h2-dark">
              {isTenant
                ? <><span>Your rental,</span><br />right in your pocket.</>
                : <><span>Your properties,</span><br />right in your pocket.</>}
            </h2>
            <p className="lp-section-sub lp-sub-dark">
              {isTenant
                ? 'Check your rent, find your lease, and submit maintenance — no calls, no emails, just everything you need about your rental right on your phone.'
                : 'RSIDE comes with a mobile app. Track rent, check your portfolio, manage work orders, and stay on top of everything — without being at your desk.'}
            </p>
            <ul className="lp-feature-list">
              {(isTenant ? TENANT_BULLETS : MANAGER_BULLETS).map(t => (
                <li key={t}><FaCheck className="lp-fl-check" />{t}</li>
              ))}
            </ul>
          </div>

        </div>
      </section>

      {/* ── TENANT EXPERIENCE ── */}
      <section className="lp-tenant-exp" id="tenant-experience">
        <div className="lp-section-inner lp-split-section">
          <div className="lp-split-left">
            <div className="lp-badge lp-badge-light">
              <span className="lp-badge-dot lp-dot-teal" />
              FOR YOUR TENANTS
            </div>
            <h2 className="lp-section-h2 lp-h2-dark">Give tenants a home<br />base they'll actually use.</h2>
            <p className="lp-section-sub lp-sub-dark">
              When you invite a tenant, they get their own portal tied directly to their unit.
              Everything they need — rent, their lease, property contacts, maintenance status — is right there waiting for them.
            </p>
            <ul className="lp-feature-list">
              {[
                'No more "when is rent due?" texts',
                'Maintenance requests go straight into your queue',
                'Tenants see exactly what you want them to see',
                'Works on any device — mobile or desktop',
              ].map(t => (
                <li key={t}><FaCheck className="lp-fl-check" />{t}</li>
              ))}
            </ul>
            <button className="lp-btn-outline-dark" onClick={() => navigate('/signup')}>
              Set Up Your Manager Workspace <FaArrowRight style={{ fontSize: 11 }} />
            </button>
          </div>

          <div className="lp-split-right">
            <div className="lp-section-img-wrap">
              <img src={tenantHome} alt="RSIDE tenant home screen" className="lp-section-img" />
            </div>
          </div>
        </div>
      </section>

      {/* ── FEATURE GRID ── */}
      <section className="lp-features" id="features">
        <div className="lp-section-inner">
          <div className="lp-section-header lp-header-center">
            <div className="lp-badge lp-badge-light">
              <span className="lp-badge-dot lp-dot-purple" />
              ONE CONNECTED WORKSPACE
            </div>
            <h2 className="lp-section-h2 lp-h2-dark">The details that matter,<br />in one place.</h2>
            <p className="lp-section-sub lp-sub-dark">
              RSIDE keeps the pieces of your rental work connected so you're not rebuilding context every time you switch tasks.
            </p>
          </div>

          <div className="lp-feat-grid">
            {FEATURES.map((f) => (
              <div key={f.title} className="lp-feat-card">
                <div className="lp-feat-top">
                  <div className="lp-feat-icon" style={{ background: `${f.color}20`, color: f.color }}>{f.icon}</div>
                  <span className="lp-feat-num">{f.num}</span>
                </div>
                <h3 className="lp-feat-title">{f.title}</h3>
                <p className="lp-feat-desc">{f.desc}</p>
                <div className="lp-feat-tag"><span className="lp-feat-tag-line" style={{ background: f.color }} />{f.tag}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="lp-faq" id="faq">
        <div className="lp-section-inner lp-faq-inner">
          <div className="lp-faq-left">
            <div className="lp-badge lp-badge-light">
              <span className="lp-badge-dot lp-dot-purple" />
              FAQ
            </div>
            <h2 className="lp-section-h2 lp-h2-dark">Questions,<br />answered plainly.</h2>
            <p className="lp-section-sub lp-sub-dark">A quick look at how RSIDE works for managers and tenants.</p>
            <div className="lp-faq-contact">
              <FaComments className="lp-faq-contact-icon" />
              <div>
                <div>Have a question that's not here? Reach out to the team.</div>
                <div className="lp-faq-contact-detail">
                  <strong>RSIDE / RSIDE Tenant</strong><br />
                  <a href="mailto:support@dtecx.net" className="lp-faq-contact-email">support@dtecx.net</a>
                </div>
              </div>
            </div>
          </div>
          <div className="lp-faq-right">
            {FAQ_ITEMS.map((f, i) => (
              <FaqItem key={f.q} q={f.q} a={f.a} defaultOpen={i === 0} />
            ))}
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section className="lp-final-cta">
        <div className="lp-final-cta-inner">
          <div className="lp-badge lp-badge-dark">
            <span className="lp-badge-dot lp-dot-green" />
            OPEN BETA — FREE TO GET STARTED
          </div>
          <h2 className="lp-final-h2">Put every rental relationship<br />on the same side.</h2>
          <p className="lp-final-sub">Managers stay on top of everything. Tenants know what's happening with their rental. Everyone's working from the same information.</p>
          <div className="lp-final-ctas">
            <button className="lp-btn-final-primary" onClick={() => navigate('/signup')}>
              Get Started as a Manager <FaArrowRight style={{ fontSize: 12 }} />
            </button>
            <button className="lp-btn-final-ghost" onClick={() => navigate('/tenant/signup')}>
              Sign Up as a Tenant <FaKey style={{ fontSize: 12 }} />
            </button>
          </div>
          <div className="lp-final-note"><FaCheck style={{ fontSize: 10 }} /> No credit card required</div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="lp-footer">
        <div className="lp-footer-inner">
          <div className="lp-footer-brand">
            <div className="lp-footer-logo-row">
              <img src={logo} alt="RSIDE" className="lp-footer-logo" />
            </div>
            <p className="lp-footer-brand-desc">One connected place to run rental properties and give tenants a simpler experience.</p>
            <div className="lp-footer-badge">
              <span className="lp-badge-dot lp-dot-green" style={{ width: 8, height: 8 }} />
              Open Beta — Free to Get Started
            </div>
          </div>

          <div className="lp-footer-links">
            <div className="lp-footer-col">
              <div className="lp-footer-col-title">PRODUCT</div>
              <button className="lp-footer-link" onClick={() => scrollTo('for-managers')}>For Managers</button>
              <button className="lp-footer-link" onClick={() => scrollTo('tenant-experience')}>For Tenants</button>
              <button className="lp-footer-link" onClick={() => scrollTo('features')}>Features</button>
              <button className="lp-footer-link" onClick={() => scrollTo('how-it-works')}>How It Works</button>
              <button className="lp-footer-link" onClick={() => scrollTo('faq')}>FAQ</button>
            </div>
            <div className="lp-footer-col">
              <div className="lp-footer-col-title">ACCESS</div>
              <button className="lp-footer-link" onClick={() => navigate('/login')}>Manager Log In</button>
              <button className="lp-footer-link" onClick={() => navigate('/tenant/login')}>Tenant Log In</button>
              <button className="lp-footer-link" onClick={() => navigate('/signup')}>Manager Sign Up</button>
              <button className="lp-footer-link" onClick={() => navigate('/tenant/signup')}>Tenant Sign Up</button>
            </div>
            <div className="lp-footer-col">
              <div className="lp-footer-col-title">COMPANY</div>
              <button className="lp-footer-link" onClick={() => scrollTo('faq')}>About RSIDE</button>
              <button className="lp-footer-link" onClick={() => scrollTo('faq')}>Contact</button>
              <button className="lp-footer-link" onClick={() => navigate('/privacy')}>Privacy</button>
              <button className="lp-footer-link" onClick={() => navigate('/terms')}>Terms</button>
            </div>
          </div>
        </div>
        <div className="lp-footer-bottom">
          <span>© 2026 RSIDE. Rental operations, on the same side.</span>
          <span className="lp-footer-tagline">Built for better property experiences</span>
        </div>
      </footer>

    </div>
  );
}

export default LandingPage;
